/**
 * Pagination — accessible page-nav control.
 *
 * Shows: « prev  1 … 4 [5] 6 … 12  next »
 * Keyboard: left/right arrow keys on the control navigate pages.
 */

import type { KeyboardEvent } from 'react'

interface PaginationProps {
  page: number       // 1-based current page
  total: number      // total number of items
  pageSize: number   // items per page
  onChange: (page: number) => void
  className?: string
}

function range(start: number, end: number): number[] {
  const out: number[] = []
  for (let i = start; i <= end; i++) out.push(i)
  return out
}

function buildPages(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return range(1, total)

  const left  = Math.max(2, current - 1)
  const right = Math.min(total - 1, current + 1)
  const pages: Array<number | '…'> = [1]

  if (left > 2) pages.push('…')
  range(left, right).forEach((p) => pages.push(p))
  if (right < total - 1) pages.push('…')
  pages.push(total)
  return pages
}

export function Pagination({
  page,
  total,
  pageSize,
  onChange,
  className = '',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  if (totalPages <= 1) return null

  const pages = buildPages(page, totalPages)

  function handleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft'  && page > 1)           onChange(page - 1)
    if (e.key === 'ArrowRight' && page < totalPages)  onChange(page + 1)
  }

  const BASE_BTN = 'inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-[6px] border text-xs font-medium cursor-pointer select-none transition-colors duration-[120ms] disabled:opacity-[0.28] disabled:cursor-not-allowed'
  const ACTIVE_BTN = 'bg-[rgba(6,182,212,0.14)] text-[#06b6d4] border-[rgba(6,182,212,0.25)] font-bold cursor-default'
  const INACTIVE_BTN = 'bg-transparent border-transparent text-[#8fa3bd] hover:bg-[#101a2b] hover:text-[#e2ecf9] hover:border-[rgba(148,163,184,0.18)]'

  return (
    <nav
      className={`flex items-center gap-0.5 ${className}`.trim()}
      aria-label="Pagination"
      onKeyDown={handleKey}
    >
      <button
        className={`${BASE_BTN} ${INACTIVE_BTN}`}
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        ‹
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} className="inline-flex items-center justify-center min-w-[28px] h-7 text-xs text-[#4a6280] select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            className={`${BASE_BTN} ${p === page ? ACTIVE_BTN : INACTIVE_BTN}`}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onChange(p as number)}
          >
            {p}
          </button>
        ),
      )}

      <button
        className={`${BASE_BTN} ${INACTIVE_BTN}`}
        aria-label="Next page"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        ›
      </button>
    </nav>
  )
}
